import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { ErrorService } from 'services/error.service';
import { ModifierGroupService } from 'services/modifier-group.service';
import { UiService } from 'services/ui.service';
import { V2ScrollableContentBottomSheetRef } from 'src/app/v2-components/helpers/v2-bottom-sheet-ref';
import { ItemDetailsStore } from 'stores/item-details.store';
import { LocationStore } from 'stores/location.store';
import { AsyncUtil } from 'utility/async.util';
import { RxUtil } from 'utility/rx.util';
import { IOrderMenuItem } from '../../models/order-menu.models';
import { OrderMenuItemPinningStore } from 'stores/order-menu-item-pinning.store';
import { OrderMenuItemPinningService } from 'services/order-menu-item-pinning.service';
import { OrderPayStore } from 'stores/order-pay.store';
import { NavController } from '@ionic/angular';
import { OrderMenuService } from 'services/order-menu.service';
import { BottomSheetService } from 'src/app/v2-components/services/bottom-sheet.service';
import { ReadonlyItemDetailsStore } from 'stores/readonly-item-details-store.store';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'readonly-item-details-sheet',
  templateUrl: 'readonly-item-details.sheet.html',
  styleUrls: ['readonly-item-details.sheet.scss']
})
export class ReadonlyItemDetailsSheet
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('backdropImageWrapper', { read: ElementRef })
  private backdropImageWrapper?: ElementRef;

  @ViewChild('contentHeaderTitle', { read: ElementRef })
  private contentHeaderTitle?: ElementRef;
  private contentTitleObserver?: IntersectionObserver;
  private currentItem?: IOrderMenuItem;

  readonly loadingItemModifiers$ = new BehaviorSubject<boolean>(true);
  showTitleInHeader: boolean = false;
  hasParentSheet: boolean = false;

  constructor(
    public itemDetailsStore: ItemDetailsStore,
    private readonlyItemDetailsStore: ReadonlyItemDetailsStore,
    public uiService: UiService,
    public modifierGroupService: ModifierGroupService,
    private locationStore: LocationStore,
    private errorService: ErrorService,
    private bottomSheetRef: V2ScrollableContentBottomSheetRef<boolean>,
    private bottomSheetService: BottomSheetService,
    public orderMenuItemPinningStore: OrderMenuItemPinningStore,
    private orderMenuItemPinningService: OrderMenuItemPinningService,
    private orderPayStore: OrderPayStore,
    private navController: NavController,
    private orderMenuService: OrderMenuService,
    private translateService: TranslateService
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      this.currentItem = await RxUtil.takeOne(
        this.itemDetailsStore.selectedItem$
      );
      this.hasParentSheet = await RxUtil.takeOne(
        this.readonlyItemDetailsStore.hasParentSheet$
      );

      if (this.currentItem?.hasModifiers) {
        await this.modifierGroupService.loadModifierGroupsForSelectedItem();
      }
    } catch (e) {
      await this.errorService.performDefaultErrorHandling(
        e,
        'Failed to load all item details'
      );
    } finally {
      this.loadingItemModifiers$.next(false);
    }
  }

  async ngAfterViewInit(): Promise<void> {
    // wait here to give page enough time to render before checking title visibility
    await AsyncUtil.sleep(100);
    if (this.currentItem) {
      this.readonlyItemDetailsStore.pushToStack(this.currentItem.id);
    }

    this.contentTitleObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        if (entries.length > 0) {
          this.showTitleInHeader = entries[0].intersectionRatio < 0.35;
        }
      },
      {
        root: document.getElementById('insertion-cont'),
        threshold: [0, 0.25, 0.5, 0.75, 1],
        rootMargin: this.currentItem?.image.url
          ? '-50px 0px 0px 0px'
          : undefined
      }
    );
    this.contentTitleObserver.observe(this.contentHeaderTitle?.nativeElement);
  }

  ngOnDestroy(): void {
    this.contentTitleObserver?.disconnect();
    this.close();
  }

  close() {
    this.bottomSheetRef.close();
  }

  async onBackButtonClick(item: IOrderMenuItem): Promise<void> {
    let parentItemId = this.readonlyItemDetailsStore.popStack();
    this.itemDetailsStore.clearSelectedItem();
    this.close();
    if (parentItemId === item.id) {
      parentItemId = this.readonlyItemDetailsStore.popStack();
    }
    if (parentItemId) {
      this.itemDetailsStore.selectedItemId = parentItemId;
      this.bottomSheetService.showBottomSheet(ReadonlyItemDetailsSheet);
    }
  }

  readonly showOpaqueSheetHeader$: Observable<boolean> =
    this.bottomSheetRef.onContentScroll$.pipe(
      map(
        (scroll) =>
          scroll >=
          (this.backdropImageWrapper?.nativeElement?.offsetHeight ?? 0) - 60
      )
    );

  readonly itemRecommendationHeaderText$: Observable<string> = combineLatest([
    this.locationStore.currentLocation$,
    this.translateService.get('Menu.Items.RecommendedPairings')
  ]).pipe(
    map(([location, i18nRecommendedPairings]) =>
      location.orderReviewPageSettings?.itemRecommendationHeaderText
        ? location.orderReviewPageSettings.itemRecommendationHeaderText
        : i18nRecommendedPairings.toUpperCase()
    )
  );

  readonly itemRecommendationSecondaryText$: Observable<string | null> =
    this.locationStore.currentLocation$.pipe(
      map((location) =>
        location.orderReviewPageSettings?.itemRecommendationSecondaryText
          ? location.orderReviewPageSettings.itemRecommendationSecondaryText
          : null
      )
    );

  // following same rules as Review Order page, limit the number of recommended pairing to 5.
  readonly recommendedItems$: Observable<IOrderMenuItem[]> =
    this.itemDetailsStore.selectedItem$.pipe(
      map((item) =>
        item.recommendedItems.filter((ri) => ri.inStock).slice(0, 5)
      )
    );

  async togglePinMenuItem(event: Event, item: IOrderMenuItem) {
    await this.orderMenuItemPinningService.togglePinMenuItem(item.id);
    event.stopPropagation();
  }

  openLightbox(): Promise<boolean> {
    const selectedItemId = this.itemDetailsStore.selectedItemId;
    this.bottomSheetRef.close(true);
    return this.navController.navigateForward(
      [
        '/',
        this.orderPayStore.beaconId,
        'v2',
        'order',
        'item-details',
        'lightbox'
      ],
      { queryParams: { readonly: true, selectedItemId } }
    );
  }

  async onItemRecommendationClick(item: IOrderMenuItem): Promise<void> {
    this.itemDetailsStore.clearSelectedItem();
    this.bottomSheetRef.close();
    this.orderMenuService.viewReadonlyItemDetails(item);
  }
}
